import { useCallback, useEffect, useRef, useState } from 'react';
import { syncSchoolCachesWithQuery } from '../query/syncSchoolCachesWithQuery';
import {
  getSchoolDataLoginHeadSyncGeneration,
  SCHOOL_DATA_LOGIN_HEAD_SYNC_EVENT,
} from '../utils/schoolDataLoginHeadSync';

/**
 * `workspaces.updated_at` head 비교 후 ready.
 * 로그인·새로고침 플래그(또는 이벤트)가 오면 keep-alive여도 다시 sync.
 */
export function useSchoolScopedCacheSyncReady(
  workspaceId: string,
  isActive = true
): {
  schoolScopedCacheSyncReady: boolean;
  schoolScopedCacheSyncPending: boolean;
} {
  const [ready, setReady] = useState(() => !workspaceId.trim());
  const [pending, setPending] = useState(() => !!workspaceId.trim());
  const lastSyncedGenRef = useRef(0);
  const syncingRef = useRef(false);

  useEffect(() => {
    setReady(!workspaceId.trim());
    setPending(!!workspaceId.trim());
    lastSyncedGenRef.current = 0;
  }, [workspaceId]);

  const runSync = useCallback(async () => {
    const sid = workspaceId.trim();
    if (!sid) {
      setReady(true);
      setPending(false);
      lastSyncedGenRef.current = getSchoolDataLoginHeadSyncGeneration();
      return;
    }
    if (syncingRef.current) return;
    syncingRef.current = true;
    setPending(true);
    try {
      await syncSchoolCachesWithQuery(sid);
    } catch {
      /* sync 실패 시에도 ready — 각 query가 자체 에러 처리 */
    } finally {
      syncingRef.current = false;
      lastSyncedGenRef.current = getSchoolDataLoginHeadSyncGeneration();
      setReady(true);
      setPending(false);
    }
  }, [workspaceId]);

  // ready=false 이면 sync (최초·workspace 변경·로그인 재트리거). keep-alive 비활성 중이어도 수행.
  useEffect(() => {
    if (ready) return;
    void runSync();
  }, [ready, runSync]);

  // 로그인·새로고침: generation 상승 시 ready 리셋 → 위 effect가 재동기화
  useEffect(() => {
    const bumpIfNeeded = () => {
      const gen = getSchoolDataLoginHeadSyncGeneration();
      if (gen > lastSyncedGenRef.current) {
        setReady(false);
      }
    };
    bumpIfNeeded();
    window.addEventListener(SCHOOL_DATA_LOGIN_HEAD_SYNC_EVENT, bumpIfNeeded);
    return () => window.removeEventListener(SCHOOL_DATA_LOGIN_HEAD_SYNC_EVENT, bumpIfNeeded);
  }, [workspaceId]);

  // isActive 복귀 시 놓친 login sync가 있으면 재시도
  useEffect(() => {
    if (!isActive) return;
    const gen = getSchoolDataLoginHeadSyncGeneration();
    if (gen > lastSyncedGenRef.current) {
      setReady(false);
    }
  }, [isActive, workspaceId]);

  return {
    schoolScopedCacheSyncReady: ready,
    schoolScopedCacheSyncPending: pending,
  };
}
